import { useNavigate } from "react-router";
import { NavigationBarMenu } from "../../../components/navigation/NavigationBar";
import {
  DISCOVER,
  SELLAR_CREDIT_MANAGE_COMPANY,
  SELLAR_CREDIT_TAB,
} from "../../../constants/routes";
import { Button, Icon } from "@sellar-io/components";
import { withSearchParams } from "../../../utils/withSearchParams";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useLegalEntityCompanyCreditInfo } from "../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { useLegalEntity } from "../../../hooks/api/v2/legalEntity/useLegalEntity";
import { useLegalEntityId } from "../../../hooks/api/v2/legalEntity/useLegalEntityId";
import { CompanyCreditInfoResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useSellarUser } from "../../../hooks";
import {
  PromptLegalEntityAdminVerificationDialog,
  TwoVerificationDialog,
} from "../../../components/twoSellarCreditVerificationRequiredAlert/TwoSellarCreditVerificationRequiredAlert";
import { useIntercom } from "react-use-intercom";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { Alert } from "../../../components/alert/Alert";

export const SellarCreditProviderSelection = () => {
  const { isActive } = useRemoteFeatureFlags();
  const navigate = useNavigate();

  const { user } = useSellarUser();

  const { business } = useSellarCurrentBusiness();

  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    enabled: !!legalEntityId,
  });

  const companyHasHokodoAccount = !!legalEntity?.hokodoCompanyId;

  const isUserBusinessLegalEntityAdmin = !!user?.legalEntityAdminList.find(
    (legalEntityAdmin) => legalEntityAdmin.legalEntityId === legalEntityId,
  );

  const { data: creditInfo } = useLegalEntityCompanyCreditInfo({
    RetailerId: business?.Retailer?.id ?? 0,
  });
  const isUserSellarAdmin = user?.type === "admin";

  return (
    <>
      <NavigationBarMenu
        title={"Sellar Credit"}
        rightComponent={
          (isUserSellarAdmin || isUserBusinessLegalEntityAdmin) && (
            <Button
              color="slim"
              className="flex items-center gap-1"
              onClick={() => navigate(SELLAR_CREDIT_MANAGE_COMPANY)}
            >
              <Icon icon="COG" size={0.7} />
              <span className="hidden sm:block">Manage company</span>
            </Button>
          )
        }
      />

      <div className="flex flex-col justify-center px-4 py-4 sm:py-8 md:py-24">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-8">
          <Heading
            creditInfo={creditInfo ?? null}
            isUserBusinessLegalEntityAdmin={isUserBusinessLegalEntityAdmin}
          />

          <div className="w-full border border-b-zinc-300" />

          <div className="flex w-full max-w-6xl flex-wrap justify-between gap-8">
            {companyHasHokodoAccount && (
              <>
                {/* Hokodo element   */}
                <div className="flex grow basis-sm flex-col justify-between">
                  <div className="mb-4 flex flex-col gap-1">
                    <div className="text-lg font-semibold text-zinc-800">
                      Your Hokodo account
                    </div>
                    <div className="text-base text-zinc-500">
                      Click the button below to see your previous orders and
                      invoices with Hokodo
                    </div>
                  </div>

                  {/* Hokodo tab button   */}

                  <button
                    type="button"
                    onClick={() => {
                      navigate(
                        withSearchParams(SELLAR_CREDIT_TAB, {
                          provider: "hokodo",
                        }),
                      );
                    }}
                    className="flex cursor-pointer flex-col gap-6 rounded-lg border border-zinc-300 bg-white p-8 hover:bg-zinc-50 hover:font-semibold"
                  >
                    <div className="flex items-start">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-100">
                        <Icon
                          icon="HOKODO_ICON"
                          className="text-zinc-800"
                          size={0.8}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>Hokodo tab</div>
                      <Icon icon="ARROW_RIGHT" size={1} />
                    </div>
                  </button>
                </div>
              </>
            )}

            {/* Two element   */}

            {creditInfo?.status === "approved" &&
              creditInfo.creditProvider === "two" && (
                <div className="flex grow basis-sm flex-col justify-between">
                  <div className="mb-4 flex flex-col gap-1">
                    <div className="text-lg font-semibold text-zinc-800">
                      Your Two account
                    </div>
                    <div className="text-base text-zinc-500">
                      Click the button below to see your current orders and
                      invoices on your Two dashboard
                    </div>
                  </div>

                  {/* Two tab button   */}

                  <button
                    type="button"
                    onClick={() => {
                      if (isActive("twoSellarCreditDashboard")) {
                        return navigate(
                          withSearchParams(SELLAR_CREDIT_TAB, {
                            provider: "two",
                          }),
                        );
                      }

                      const url =
                        import.meta.env.VITE_ENV === "production"
                          ? `https://buyer.two.inc/login`
                          : `https://buyer.sandbox.two.inc/login`;

                      // open two buyer portal in new tab
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    className="flex cursor-pointer flex-col gap-6 rounded-lg border border-zinc-300 bg-white p-8 hover:bg-zinc-50 hover:font-semibold"
                  >
                    <div className="flex items-start">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-100">
                        <Icon
                          icon="TWO_ICON"
                          className="text-zinc-800"
                          size={1.5}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>Two tab</div>
                      <Icon icon="ARROW_RIGHT" size={1} />
                    </div>
                  </button>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

const Heading = ({
  creditInfo,
  isUserBusinessLegalEntityAdmin,
}: {
  creditInfo: CompanyCreditInfoResponseType | null;
  isUserBusinessLegalEntityAdmin: boolean;
}) => {
  const [activeDialog, setActiveDialog] = useState<
    "changeUser" | "verifyTwoAccount"
  >();

  const navigate = useNavigate();
  const intercom = useIntercom();

  // user has successfully created and verified two user account

  if (
    creditInfo?.creditProvider === "two" &&
    creditInfo.status === "approved" &&
    creditInfo.isCompanyVerified
  ) {
    return (
      <div className="flex flex-col items-center justify-center">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/app-assets/sellar-approve-badge.png"
          className="w-[77px]"
          width={77}
          height={77}
        />
        <div className="mb-4 text-2xl font-semibold text-zinc-800">
          Thanks for completing sign up with Two Inc
        </div>

        <div className="text-base text-zinc-800">
          Your new Sellar Credit tab area is coming soon.
        </div>
      </div>
    );
  }

  // user needs to complete 'two' user account verification

  if (
    creditInfo?.creditProvider === "two" &&
    creditInfo.status === "approved" &&
    !creditInfo.isCompanyVerified
  ) {
    return (
      <>
        <div className="flex max-w-6xl flex-col items-center justify-center">
          <div className="mb-4 rounded-full border border-orange-300 bg-orange-100 p-4">
            <Icon size={1.2} icon={"CAUTION"} className="text-orange-800" />
          </div>

          <div className="mb-2 text-2xl font-semibold text-zinc-800">
            You need to complete verification with Two
          </div>

          <div className="mb-4 text-base text-zinc-600">
            To keep using Sellar Credit, you need to complete verification with
            our new credit provider, Two Inc.
          </div>

          <Button
            onClick={() => {
              if (isUserBusinessLegalEntityAdmin) {
                return setActiveDialog("verifyTwoAccount");
              }

              setActiveDialog("changeUser");
            }}
          >
            Verify account
          </Button>
        </div>

        <PromptLegalEntityAdminVerificationDialog
          active={activeDialog === "changeUser"}
          onDismiss={() => setActiveDialog(undefined)}
        />

        <TwoVerificationDialog
          active={activeDialog === "verifyTwoAccount"}
          onDismiss={() => setActiveDialog(undefined)}
        />
      </>
    );
  }

  // two no credit check performed

  if (creditInfo?.status === "creditCheckNotPerformed") {
    return (
      <>
        <div className="flex flex-col items-center justify-center">
          <div className="mb-4 rounded-full border border-orange-300 bg-orange-100 p-4">
            <Icon size={1.2} icon={"CAUTION"} className="text-orange-800" />
          </div>

          <div className="mb-2 text-2xl font-semibold text-zinc-800">
            No Sellar Credit information found
          </div>

          <div className="mb-4 text-base text-zinc-600">
            To see if you qualify for Sellar Credit, click the button below.
          </div>

          <Button
            onClick={() => {
              navigate(`${DISCOVER}?showSellarCreditOnboarding=true`);
            }}
          >
            Get started
          </Button>
        </div>
      </>
    );
  }

  // declined

  if (["declined"].includes(creditInfo?.status ?? "")) {
    return (
      <>
        <div className="flex flex-col items-center justify-center">
          <div className="mb-4 rounded-full border border-yellow-300 bg-yellow-100 p-4">
            <Icon
              size={1.2}
              icon={"TEXT_BOX_SEARCH_OUTLINE"}
              className="text-yellow-800"
            />
          </div>

          <div className="mb-2 text-2xl font-semibold text-zinc-800">
            We need more information to see if you’re eligible for credit with
            Two
          </div>

          <div className="mb-8 text-center text-base text-zinc-600">
            Our trade credit provider, Two Inc, requires more information before
            they can offer you a potential credit limit. Use the credit builder
            tool below to provide the information needed.
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            <Button
              className={"w-48 p-4"}
              onClick={() => {
                window.open(
                  "https://checkout.two.inc/credit_limit_change/credit_builder",
                  "_blank",
                );
              }}
            >
              Start Credit Builder
            </Button>

            <Button
              color={"secondary"}
              className={"w-48 p-4"}
              onClick={() => intercom.show()}
            >
              <div className="flex items-center gap-1">
                <Icon
                  icon="HELP_CIRCLE_OUTLINE"
                  size={1}
                  className="shrink-0"
                />
                Contact support
              </div>
            </Button>
            {/* creditLimit of not null indicates two merchant customer exists */}
            {creditInfo?.creditLimit !== null && (
              <Button
                color={"secondary"}
                className={"w-48 p-4"}
                onClick={() =>
                  navigate(
                    withSearchParams(SELLAR_CREDIT_TAB, { provider: "two" }),
                  )
                }
              >
                View statements
              </Button>
            )}
          </div>

          <div className="py-8">
            <Alert
              title="What to do if you’ve already used Two’s credit builder tool"
              message="If you’ve already submitted your information to Two’s credit builder tool and have been denied trade credit, unfortunately you will not be able to use Sellar Credit at this time. You may check again in the future as this may change."
            />
          </div>
        </div>
      </>
    );
  }
};
